import React from "react";
import {
	View,
	Platform
} from "react-native";

import HorizontalRule from "../HorizontalRule";
import StyledText from "../StyledText";
import FileItem from "../FileItem";
import Link from "../Link";
import PDFViewer from "../PDFViewer";
import mime from "react-native-mime-types";
import SimpleMarkdown from "simple-markdown";

var SizeMap = {
	1: "large",
	2: "smallHeading"
};

var DEFAULT_SIZE = "emphasized";

const IS_WEB = Platform.OS === "web";

const textBreak = Platform.OS === "web" ? <div style={{ flexBasis: "100%", height: 0 }}></div> : <StyledText style={{ flexBasis: "100%", height: 0 }}></StyledText>;

export default function (styles, extra) {
	return {
		pdf: {
			order: 23,
			match: function (source) {
				return /^\!\<([\s\S]+\.pdf)\>\!/.exec(source);
			},
			parse: function (capture) {
				return {
					content: capture[1]
				};
			},
			react: function Pdf(node) {
				if (Platform.OS === "web")
					return React.createElement(PDFViewer, {
						url: node.content,
						width: extra.width,
						fullscreen: !extra.noPdfFullScreen,
						noDownload: extra.contrast
					});
				else
					return React.createElement(FileItem, {
						file: {
							url: node.content,
							format: "pdf"
						}
					});
			}
		},
		paragraph: {
			react: function Paragraph(node, output, state) {
				var component = Platform.OS === "web" ? StyledText : View;
				return React.createElement(component, {
					style: styles.paragraph
				}, output(node.content, state));
			}
		},
		heading: {
			react: function Heading(node) {
				var text = findText(node);
				var level = node.level;
				var size = SizeMap[level] || DEFAULT_SIZE;
				return React.createElement(StyledText, {
					size: size,
					bold: true,
					tertiary: extra.contrast
				}, text);
			}
		},
		centeredHeading: {
			order: SimpleMarkdown.defaultRules.heading.order,
			match: SimpleMarkdown.blockRegex(/^ *><(#{1,6})([^\n]+?)#* *(?:\n *)+\n/),
			parse: SimpleMarkdown.defaultRules.heading.parse,
			react: function Heading(node) {
				var text = findText(node);
				var level = node.level;
				var size = SizeMap[level] || DEFAULT_SIZE;
				return React.createElement(StyledText, {
					size: size,
					bold: true,
					tertiary: extra.contrast,
					style: {
						textAlign: "center"
					}
				}, text);
			}
		},
		em: {
			react: function Italics(node, output, state) {
				return React.createElement(StyledText, {
					italic: true,
					tertiary: extra.contrast
				}, output(node.content, state));
			}
		},
		u: {
			react: function Underline(node, output, state) {
				return React.createElement(StyledText, {
					underline: true,
					tertiary: extra.contrast
				}, output(node.content, state));
			}
		},
		strong: {
			react: function Strong(node, output, state) {
				return React.createElement(StyledText, {
					bold: true,
					tertiary: extra.contrast
				}, output(node.content, state));
			}
		},
		image: {
			react: function Image(node) {
				var url = node.target;
				var mimeType = mime.lookup(url);
				return React.createElement(FileItem, {
					file: {
						url: url,
						format: mimeType
					}
				});
			}
		},
		hr: {
			react: function Hr() {
				return React.createElement(HorizontalRule);
			}
		},
		/**
		 * Q: Why is text rendered as individual words?
		 *
		 * A: It's because simple-markdown parses blocks of text on special symbol.
		 * "Ne prenez pas d'alcool. Ne fumez pas"
		 * turns into
		 * ["Ne prenez pas d", "'alcool", ". Ne fumez pas"]
		 *
		 * If the strings are rendered inside of 3 <Text/> it ends up something like this
		 * |-----------------|
		 *  Ne prenez pas d
		 *  'alcool
		 *  . Ne fumez pas
		 *
		 * instead of
		 *
		 * |-----------------|
		 *  Ne prenez pas d
		 *  'alcool. Ne fumez
		 *  pas
		 *
		 * because all of the <Text/> are rendered as "blocks" instead of "span"-like elements
		 *
		 * The only way to achieve span-like rendering in React Native is to put <Text/> inside of a <Text/>
		 * but we sometimes cannot guarantee that AND <Text><View/></Text> is INVALID due to restrictions in RN
		 *
		 * So to emulate that, we render each word individually and let flexbox handle it.
		 */
		text: {
			react: function Text(node) {
				let retWords = [];
				let start = 0;
				let i = 0;
				let str = node.content;

				const addWord = (str) => {
					let newWord = React.createElement(StyledText, {
						tertiary: extra.contrast
					}, str);
					retWords.push(newWord);

				};

				//for integrated logic to trim spaces after new line
				let afterNewline = false;

				while (i < str.length) {
					let end = i + 1;
					if (str.charAt(i) === " " || i === str.length - 1) {
						if (!afterNewline) {
							let subStr = str.substring(start, end);
							addWord(subStr);
						}
						start = end;
					}

					else if (str.charAt(i) === "\n") {
						let subStr = str.substring(start, i);
						addWord(subStr);
						retWords.push(textBreak);
						start = end;
						afterNewline = true;
					}
					else {
						afterNewline = false;
					}
					i++;
				}

				return retWords;
			}
		},
		newline: {
			react: function NewLine() {
				return React.createElement(StyledText, {}, "\n");
			}
		},
		br: {
			react: function Br() {
				return React.createElement(StyledText, {}, "\n\n");
			}
		},
		del: {
			react: function Del(node, output, state) {
				return React.createElement(StyledText, {
					strikethrough: true,
					tertiary: extra.contrast
				}, output(node.content, state));
			}
		},
		inlineCode: {
			react: function InlineCode(node) {
				return React.createElement(StyledText, {
					style: styles.inlineCode
				}, node.content);
			}
		},
		link: {
			react: function MLink(node) {
				var text = findText(node);
				return React.createElement(Link, {
					uri: node.target,
					contrast: extra.contrast
				}, text);
			}
		},
		list: {
			react: function List(node, output, state) {
				var items = node.items.map(function (item, index) {
					var bullet = null;
					if (node.ordered)
						bullet = React.createElement(StyledText, {
							tertiary: extra.contrast
						}, (index + 1) + ". ");
					else
						bullet = React.createElement(StyledText, {
							tertiary: extra.contrast
						}, "\u2022 ");

					const content = React.createElement(View, {
						key: index,
						style: styles.listItemContent
					}, output(item, state));

					return React.createElement(View, {
						key: index,
						style: styles.listItem
					}, [bullet, content]);
				});
				return [textBreak, React.createElement(View, { style: styles.listContainer }, items)];
			}
		},
		mailto: {
			react: defaultRender
		},
		table: {
			react: defaultRender
		},
		url: {
			react: defaultRender
		},
		autolink: {
			react: defaultRender
		},
		blockQuote: {
			react: defaultRender
		},
		codeBlock: {
			react: defaultRender
		},
	};

	function defaultRender(node, output, state) {
		return React.createElement(StyledText, {
			tertiary: extra.contrast
		}, output(node.content, state));
	}
}

function findText(block) {
	var string = "";
	var content = block.content;
	if (typeof content === "string") {
		return string += content;
	} else {
		var textNodes = content.map(function (contentBlock) {
			return findText(contentBlock);
		});
		return textNodes.join("");
	}
}
